// Multi-match picker sheet for the CookComplete "what did you use"
// phase. Opens when the user taps the "+N MORE ▾" pill on a row with
// more than one matching pantry instance. We never auto-pick even
// though 99% of the time nearest-expiring is right — the user asked
// to confirm per the kitchen reality that people reach for the wrong
// container all the time.
//
// Substitute-aware:
//   - When EVERY candidate's canonical differs from the recipe's,
//     the kicker shifts from "PICK YOUR MOZZARELLA" to "SUBSTITUTING
//     FOR MOZZARELLA" so the language reads honestly.
//   - In mixed pickers (some exact, some subs) the substitute rows
//     get a small SUBSTITUTE chip + the canonical's name prefix on
//     the line, so the cook can tell the real mozzarella apart from
//     a bowl of feta sitting in the same picker.
//
// Extracted from CookComplete.jsx 2026-05-03 to keep that component
// under the 1,500-line tripwire.

import { FONT, COLOR } from "../lib/tokens";
import { findIngredient, unitLabel } from "../data/ingredients";
import { resolvesToSameCanonical } from "../lib/recipePairing";

export default function CookCompletePicker({
  pickerForIdx,
  setPickerForIdx,
  usedItems,
  setUsedItems,
}) {
  if (pickerForIdx == null) return null;
  const row = usedItems.find(r => r.idx === pickerForIdx);
  if (!row) return null;

  const sorted = [...row.matches].sort((a, b) => {
    const ax = a.expiresAt ? a.expiresAt.getTime() : Infinity;
    const bx = b.expiresAt ? b.expiresAt.getTime() : Infinity;
    return ax - bx;
  });
  const now = Date.now();

  // Substitute-aware kicker logic — every candidate must mismatch the
  // recipe canonical for the language to flip.
  const recipeCanonId = row.canonical?.id || null;
  const allSubs = recipeCanonId
    ? sorted.length > 0 && sorted.every(m =>
        m.ingredientId && !resolvesToSameCanonical(m.ingredientId, recipeCanonId),
      )
    : false;
  const recipeName = (row.canonical?.name || row.recipeIng.item || "").toUpperCase();

  return (
    <div
      onClick={() => setPickerForIdx(null)}
      style={{ position:"absolute", inset:0, background:"#000d", zIndex:5, display:"flex", alignItems:"flex-end", animation:"rise 0.18s ease" }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width:"100%", maxHeight:"75%", overflowY:"auto", background:COLOR.deep, borderTop:`1px solid ${COLOR.border}`, borderTopLeftRadius:18, borderTopRightRadius:18, padding:"22px 20px 16px" }}
      >
        <div style={{ width:42, height:4, background:COLOR.border, borderRadius:2, margin:"0 auto 16px" }} />
        <div style={{
          fontFamily:FONT.mono, fontSize:10,
          color: allSubs ? COLOR.amber : COLOR.gold,
          letterSpacing:"0.15em", marginBottom:6,
        }}>
          {allSubs
            ? `SUBSTITUTING FOR ${recipeName}`
            : `PICK YOUR ${recipeName}`}
        </div>
        <p style={{ fontFamily:FONT.sans, fontSize:12, color:COLOR.muted, marginBottom:16 }}>
          {allSubs
            ? `${recipeName.toLowerCase().replace(/\b\w/g, c => c.toUpperCase())} isn't in your kitchen — pick the closest stand-in.`
            : `You have ${row.matches.length} containers. Which one did you pull from?`}
        </p>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {sorted.map((m, mIdx) => {
            const active = row.selectedRowId === m.id;
            const days = m.expiresAt ? Math.round((m.expiresAt.getTime() - now) / 86400000) : null;
            const dayLabel = days == null
              ? "no expiration"
              : days < 0 ? `${Math.abs(days)}d past`
              : days === 0 ? "expires today"
              : `${days}d left`;
            const dayColor = days == null ? COLOR.muted
              : days <= 1 ? COLOR.rose
              : days <= 3 ? COLOR.amber
              : COLOR.leaf;
            // Per-row substitute flag: in mixed pickers (some
            // candidates exact, some substitutes) we tag the
            // ones that aren't the recipe's canonical so the
            // cook can tell the real mozzarella apart from
            // a bowl of feta sitting in the same picker.
            const rowIsSub = !!recipeCanonId
              && !!m.ingredientId
              && !resolvesToSameCanonical(m.ingredientId, recipeCanonId);
            const mCanon = m.ingredientId ? findIngredient(m.ingredientId) : null;
            return (
              <button
                key={m.id}
                data-picker-row
                onClick={() => {
                  setUsedItems(prev => prev.map(r => r.idx === row.idx ? { ...r, selectedRowId: m.id, usedUnit: r.usedUnit || m.unit } : r));
                  setPickerForIdx(null);
                }}
                style={{
                  textAlign:"left", padding:"12px 14px",
                  background: active ? COLOR.goldDeep : COLOR.ground,
                  border: `1px solid ${active ? COLOR.gold : COLOR.border}`,
                  borderRadius:12, cursor:"pointer",
                  animation: `pickerRowIn 240ms ${mIdx * 30}ms ease-out both`,
                  transition: "background 0.15s, border-color 0.15s",
                }}
              >
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4, flexWrap:"wrap" }}>
                  <span style={{ fontFamily:FONT.mono, fontSize:10, color:COLOR.gold, letterSpacing:"0.1em" }}>
                    {(m.location || "pantry").toUpperCase()}
                  </span>
                  <span style={{ fontFamily:FONT.mono, fontSize:10, color:dayColor, letterSpacing:"0.1em" }}>
                    {dayLabel.toUpperCase()}
                  </span>
                  {rowIsSub && !allSubs && (
                    <span style={{
                      fontFamily:FONT.mono, fontSize:9, letterSpacing:"0.10em",
                      color: COLOR.amber, background: COLOR.goldDeep,
                      border: `1px solid ${COLOR.amber}33`,
                      padding: "1px 6px", borderRadius: 3,
                    }}>
                      SUBSTITUTE
                    </span>
                  )}
                  {active && <span style={{ marginLeft:"auto", fontFamily:FONT.mono, fontSize:9, color:COLOR.gold, letterSpacing:"0.1em" }}>✓ SELECTED</span>}
                </div>
                <div style={{ fontFamily:FONT.serif, fontSize:14, color:COLOR.ink, fontStyle:"italic" }}>
                  {rowIsSub && mCanon?.name ? `${mCanon.name} · ` : ""}
                  {m.amount} {unitLabel(mCanon || row.canonical, m.unit)}
                  {m.purchasedAt ? (
                    <span style={{ fontFamily:FONT.mono, fontSize:10, color:COLOR.sub, marginLeft:8, letterSpacing:"0.05em" }}>
                      · bought {m.purchasedAt.toLocaleDateString(undefined, { month:"short", day:"numeric" })}
                    </span>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
        <style>{`
          @keyframes pickerRowIn {
            0%   { opacity: 0; transform: translateY(6px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @media (prefers-reduced-motion: reduce) {
            [data-picker-row] { animation: none !important; }
          }
        `}</style>
        <button
          onClick={() => setPickerForIdx(null)}
          style={{ marginTop:14, width:"100%", padding:"12px", background:"transparent", border:`1px solid ${COLOR.border}`, borderRadius:10, fontFamily:FONT.mono, fontSize:11, color:COLOR.dim, cursor:"pointer", letterSpacing:"0.08em" }}
        >
          CLOSE
        </button>
      </div>
    </div>
  );
}
